import { useState } from "react";
import { BrandLogo } from "@/components/BrandLogo";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { InviteWelcome, useInviteRef } from "@/components/invite/InviteWelcome";

const BG = "https://images.unsplash.com/photo-1595123550441-d377e017de6a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export const safeNext = (n) => (n && /^\/read\/[\w-]+$/.test(n) ? n : "/");

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const nextQs = next === "/" ? "" : `?next=${encodeURIComponent(next)}`;
  const [form, setForm] = useState({ name: "", email: "", password: "", accept_terms: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const ref = useInviteRef(!isLogin);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await signIn(isLogin ? "/auth/login" : "/auth/register", isLogin ? { email: form.email, password: form.password } : { ...form, ref: ref || undefined });
      if (!isLogin) localStorage.removeItem("lr_ref");
      toast.success(isLogin ? "Welcome back to the archive" : "Check your email for a 6-digit code");
      navigate(next);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#0B0D10]">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={BG} alt="Ancient library" className="absolute inset-0 h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0D10]/40 via-[#0B0D10]/60 to-[#0B0D10]" />
        <div className="relative z-10 flex h-full flex-col justify-between p-14">
          <BrandLogo size="h-12 w-12" text="text-3xl" testId="auth-brand" />
          <div className="max-w-md">
            <p className="font-display text-5xl leading-[1.05] text-[#F3EBDD]">Every world deserves a keeper.</p>
            <p className="mt-6 text-[#A8A090] leading-relaxed">
              Turn scattered notes into a living lore bible, chart your continents, and trace every branch of time your story takes.
            </p>
          </div>
          <div className="lf-label">Characters · Places · Timelines · Maps</div>
        </div>
      </div>
      <div className="flex items-center px-6 sm:px-16 lf-glow">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="auth-form">
          <BrandLogo size="h-12 w-12" showText={false} testId="auth-form-logo" className="mb-6 lg:hidden" />
          <div className="lf-label mb-3">{isLogin ? "Return to your study" : "Open a new archive"}</div>
          <h1 className="font-display text-5xl text-[#F3EBDD] mb-10">{isLogin ? "Sign in" : "Create account"}</h1>
          {!isLogin && ref && <InviteWelcome code={ref} />}
          <div className="space-y-5">
            {!isLogin && (
              <div className="space-y-2">
                <Label htmlFor="name">Pen name</Label>
                <Input id="name" data-testid="auth-name-input" value={form.name} onChange={set("name")} required />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="auth-email-input" value={form.email} onChange={set("email")} required />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                {isLogin && <Link to="/forgot-password" className="text-xs text-[#A8A090] hover:text-[#F59E0B]" data-testid="forgot-password-link">Forgot password?</Link>}
              </div>
              <Input id="password" type="password" data-testid="auth-password-input" value={form.password} onChange={set("password")} required minLength={6} />
            </div>
            {!isLogin && (
              <label className="flex items-start gap-2.5 text-sm text-[#A8A090]" data-testid="auth-terms-row">
                <input type="checkbox" checked={form.accept_terms} onChange={(e) => setForm({ ...form, accept_terms: e.target.checked })} required
                  className="mt-0.5 h-4 w-4 accent-[#F59E0B]" data-testid="auth-terms-checkbox" />
                <span>I agree to the <Link to="/license" target="_blank" className="text-[#F59E0B] hover:underline" data-testid="auth-terms-link">LoreRight License &amp; Terms</Link>. I own everything I create.</span>
              </label>
            )}
            {error && <p className="text-sm text-red-400" data-testid="auth-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full h-11" data-testid="auth-submit-button">
              {loading ? "One moment…" : isLogin ? "Enter the archive" : "Create my archive"}
            </Button>
          </div>
          <p className="mt-8 text-sm text-[#A8A090]">
            {isLogin ? "New here? " : "Already have an account? "}
            <Link to={`${isLogin ? "/register" : "/login"}${nextQs}`} className="text-[#F59E0B] hover:underline" data-testid="auth-switch-link">
              {isLogin ? "Create an account" : "Sign in"}
            </Link>
          </p>
        </form>
      </div>
    </div>
  );
}
